import { ModalVisibilityProps } from 'sb-modal'
import { useForm } from 'formular'
import { useCallback, useEffect, useState } from 'sb-hooks'
import { useIntl } from 'sb-react-intl'
import request from 'sb-request'
import { constants, debounce, snowplow } from 'common/helpers'

import { required } from 'helpers/validation'
import { openReviewTagsModal } from 'compositions/modals/review/ReviewTagsModal/ReviewTagsModal'

import messages from './messages'

import reviewModalDataQuery from './reviewModalData.graphql'
import createReviewQuery from './createReview.graphql'
import updateReviewQuery from './updateReview.graphql'
import rateProductQuery from './rateProduct.graphql'


type ReviewModalDataVariables = {
  productId: string
}

type ReviewModalDataModified = {
  product: Review.Product
  userReview: Review.Review
  userRating: Review.Rating
  likes: Review.LikeTag[]
  dislikes: Review.LikeTag[]
}

type CreateUpdateVariables = {
  input: {
    reviewId?: number
    productId: number
    rating: number
    text: string
    title: string
    likes: string[]
    dislikes: string[]
    eventMetadata?: {
      placement: string
    }
  }
}

type CreateUpdatePayload = {
  createReview?: {
    reviewId: number
  }
  updateReview?: {
    result: boolean
  }
}

type RateProductVariables = {
  input: {
    productId: number
    rating: number
  }
}

// it's needed to be exctracted, because of the hooks dependencies
const submitProductRating = debounce(
  (product: Review.Product, rating: Review.Rating, onRate?: Review.ReviewModalProps['onRate']) => {
    snowplow.track(constants.snowplowEvents.productRatingSelect, {
      rating,
      productId: product.id,
      productName: product.name,
    })

    request.gql<RateProductVariables, any>('rateProduct', {
      body: {
        query: rateProductQuery,
        variables: {
          input: {
            productId: product.id,
            rating,
          },
        },
      },
      abortPrevious: true,
      withErrorModal: false,
    }).then(() => {
      if (typeof onRate === 'function') {
        onRate(product.id, rating)
      }
    })
  },
  1000
)

const useReviewModal = (props: Review.ReviewModalProps & ModalVisibilityProps) => {
  const {
    productId,
    initialRating,
    saveInitialRating,
    placement,
    source = 'not AMP',
    closeModal,
    onReviewSaved,
    onRate,
    onClose,
  } = props

  useEffect(() => {
    snowplow.track('Review editor popup', {
      productId,
      source,
      placement,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const intl = useIntl()

  const form = useForm<Review.ReviewFormValues>({
    fields: {
      rating: [ required ],
      likes: [ required ],
      dislikes: [ required ],
      title: [ required ],
      text: [ required ],
    },
    initialValues: {
      rating: null,
      likes: [],
      dislikes: [],
      title: '',
      text: '',
    },
  })

  const [ data, setData ] = useState<ReviewModalDataModified>(null)

  const { product, likes, dislikes, userReview } = data || {}

  const isFetching = !data
  const isEditing = Boolean(userReview)

  // fetching required data
  const fetchData = useCallback(async () => {
    try {
      const result = await request.gql<ReviewModalDataVariables, any, ReviewModalDataModified>('reviewModalData', {
        body: {
          query: reviewModalDataQuery,
          variables: {
            productId: String(productId),
          },
        },
        modifyResult: (data): ReviewModalDataModified => {
          const { product, likes, dislikes } = data

          if (!product) {
            return {
              product,
              userReview: null,
              userRating: null,
              likes: [],
              dislikes: [],
            }
          }

          const { userRating, userReview } = product

          return {
            product,
            userRating,
            userReview,
            likes,
            dislikes,
          }
        },
      })

      if (!result.product) {
        throw new Error(`Product ${productId} not found`)
      }

      const { product, userRating, userReview } = result
      const { title, text, likes = [], dislikes = [] } = userReview || {}

      // load initial values into the form
      form.setValues({
        rating: initialRating || userRating,
        title,
        text,
        likes,
        dislikes,
      })

      if (saveInitialRating && initialRating) {
        submitProductRating(product, initialRating, onRate)
      }

      setData(result)
    }
    catch (error) {
      console.log(error)

      closeModal()
    }
  }, [ productId, form, initialRating, saveInitialRating, closeModal, onRate ])

  // fetch data
  useEffect(() => {
    fetchData()
  }, [ fetchData ])

  // rate product
  // we should save rating on each click
  const handleRateProduct = useCallback((rating: Review.Rating) => {
    // save user rating if there is no saved review
    if (!userReview) {
      submitProductRating(product, rating, onRate)
    }
  }, [ userReview, product, onRate ])

  // submitting
  const [ isSubmitting, setSubmitting ] = useState<boolean>(false)

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitting(true)

      const values = await form.submit()

      const { rating, title, text, likes, dislikes } = values

      let reviewId = userReview ? userReview.id : null

      // for UI testing
      const variables: CreateUpdateVariables = {
        input: {
          reviewId,
          productId,
          rating,
          title,
          text,
          likes: likes.map(({ id }) => id),
          dislikes: dislikes.map(({ id }) => id),
          eventMetadata: placement ? { placement } : undefined,
        },
      }

      // create or update review
      const result = await request.gql<CreateUpdateVariables, CreateUpdatePayload>(
        reviewId ? 'updateReview' : 'createReview',
        {
          body: {
            query: reviewId ? updateReviewQuery : createReviewQuery,
            variables,
          },
          withNestedErrors: true,
        }
      )

      // save review id
      if (result.createReview) {
        reviewId = result.createReview.reviewId
      }

      if (typeof onReviewSaved === 'function') {
        onReviewSaved(productId, { ...values, id: reviewId })
      }

      closeModal()

      if (!Boolean(userReview)) {
        // if it's a first review
        openReviewTagsModal({
          productId,
          reviewId,
          rating,
          source,
          // we should update reviews after tags saving
          onTagsSaved: () => {
            if (typeof onReviewSaved === 'function') {
              onReviewSaved(productId, { ...values, id: reviewId })
            }
          },
        })
      }
    }
    catch (error) {
      console.log(error)

      setSubmitting(false)
    }
  }, [ form, userReview, productId, placement, source, onReviewSaved, closeModal ])

  // prevent unwanted closing
  const hasUnsavedChanges = useCallback(() => {
    const { title, text } = form.getValues()

    if (!userReview) {
      // for new review
      // check text fields
      return Boolean(title) || Boolean(text)
    }

    // for editing
    const { title: oldTitle, text: oldText } = userReview

    return (
      oldTitle !== title
      || oldText !== text
    )
  }, [ form, userReview ])

  const handleClose = useCallback(() => {
    if (
      hasUnsavedChanges()
      && !window.confirm(intl.message(messages.closeModal))
    ) {
      return
    }

    onClose()
  }, [ intl, hasUnsavedChanges, onClose ])

  useEffect(() => {
    // to prevent window closing
    const listener = (event) => {
      if (hasUnsavedChanges()) {
        event.returnValue = intl.message(messages.leavePage)
      }
    }

    window.addEventListener('beforeunload', listener)

    return () => {
      window.removeEventListener('beforeunload', listener)
    }
  }, [ intl, hasUnsavedChanges ])

  return {
    product,
    likes,
    dislikes,
    form,
    isEditing,
    isFetching,
    isSubmitting,
    handleSubmit,
    handleRateProduct,
    handleClose,
  }
}


export default useReviewModal
